/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useBlocker } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiDelete } from '@mdi/js';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { Button, Grid } from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import { ActivityObjectType } from '../../API';
import {
  useGetSpaceQuery,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { useGetOwnedRecipesBoardsQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
import SpaceRecipesNewSectionModal from './space_recipes_new_section_modal';
import { deduplicate } from '../../services/arrays';

const SpaceRecipesConfigView = () => {
  const navigate = useNavigate();

  const [isDirty, setIsDirty] = useState(false);

  const [newSectionModalAnchorEl, setNewSectionModalAnchorEl] = useState(null);

  const [menuTitleToRemove, setMenuTitleToRemove] = useState(null);

  const [removeSectionPopupAnchorEl, setRemoveSectionPopupAnchorEl] =
    useState(null);

  const { data: space } = useGetSpaceQuery();

  const [updateSpace] = useUpdateSpaceMutation();

  // const spaceAssignedSharedBoardIDs = space?.sharedRecipesBoardIDs || [];

  // const { data: allSharedRecipesBoards } = useGetSharedBoardsByIDsQuery(
  //   spaceAssignedSharedBoardIDs,
  //   {
  //     skip:
  //       !spaceAssignedSharedBoardIDs ||
  //       spaceAssignedSharedBoardIDs.length === 0,
  //   },
  // );

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const lanes = useMemo(
    () => (ownedRecipesBoards || []).flatMap((b) => b.menus),
    [ownedRecipesBoards],
  );

  const allMenuTitles = useMemo(
    () =>
      deduplicate((lanes || []).filter(Boolean).map((l) => l.title)).toSorted(
        (a, b) => a.toLowerCase().localeCompare(b.toLowerCase()),
      ),
    [lanes],
  );

  const publishedMenuTitles = useMemo(
    () => space?.recipesMenuTitles || [],
    [space],
  );

  const availableMenuTitles = useMemo(
    () => allMenuTitles.filter((title) => !publishedMenuTitles.includes(title)),
    [allMenuTitles, publishedMenuTitles],
  );

  const [menuTitles, setMenuTitles] = useState([
    ...publishedMenuTitles,
    ...availableMenuTitles,
  ]);

  useEffect(() => {
    setMenuTitles([...publishedMenuTitles, ...availableMenuTitles]);
  }, [publishedMenuTitles, availableMenuTitles]);

  console.log({ lanes });

  const handleDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const newMenuTitles = [...menuTitles];
    const [movedTitle] = newMenuTitles.splice(result.source.index, 1);
    newMenuTitles.splice(result.destination.index, 0, movedTitle);
    setMenuTitles(newMenuTitles);
    setIsDirty(true);
  };

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const handleSave = async () => {
    const updatedSpace = {
      ...space,
      recipesMenuTitles: menuTitles || [],
    };
    await updateSpace(updatedSpace);
    reportAccountActivitySignalOperation({
      spaceID: space.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.SPACE,
      lastActivityObjectID: null,
    });
    setIsDirty(false);
    window.setTimeout(() => navigate(-1), 300);
  };

  const onAddNewMenuTitle = (newMenuTitle) => {
    setNewSectionModalAnchorEl(null);
    setMenuTitles([...menuTitles, newMenuTitle]);
    setIsDirty(true);
  };

  const onRemoveMenuTitle = (menuTitle) => {
    setRemoveSectionPopupAnchorEl(null);
    setMenuTitleToRemove(null);
    setMenuTitles(menuTitles.filter((t) => t !== menuTitle));
    setIsDirty(true);
  };

  useBlocker(() => {
    if (isDirty) {
      // eslint-disable-next-line no-alert
      return !window.confirm(
        'You have unsaved changes. Are you sure you want to leave this page?',
      );
    }
    return false; // Allow navigation
  });

  return (
    <div
      style={{
        width: '520px',
        height: 'calc(var(--app-height) - 82px)',
        margin: 'auto',
        padding: '16px',
        overflowY: 'auto',
        backgroundColor: 'white',
        textAlign: 'left',
      }}>
      <h2>Configure Recipes sections</h2>

      <p>
        Add menus which you want to show with priority on the Recipes screen.
      </p>

      <br />

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="recipesSectionsDroppable">
          {(droppableProvided) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}>
              {menuTitles.map((menuTitle, index) => {
                return (
                  <Draggable
                    key={menuTitle}
                    draggableId={menuTitle}
                    index={index}>
                    {(provided) => (
                      <Grid
                        container
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        alignItems="center">
                        <Grid item xs={9}>
                          <Grid container>
                            <Grid
                              item
                              sx={{ display: { xs: 'none', sm: 'block' } }}
                              xs={1}>
                              &nbsp;
                            </Grid>
                            <Grid
                              item
                              xs={1}
                              flexShrink={0}
                              flexGrow={0}
                              style={{ display: 'flex', minHeight: 38 }}>
                              <div
                                {...provided.dragHandleProps}
                                style={{
                                  margin: '-4 0 0 0',
                                  // paddingTop: -4,
                                  paddingRight: 8,
                                  paddingLeft: 0,
                                }}>
                                <DragHandleIcon />
                              </div>
                            </Grid>
                            <Grid item style={{ display: 'flex' }} xs={9}>
                              {menuTitle}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={2}>
                          <Icon
                            path={mdiDelete}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              color: 'red',
                              margin: 'auto 4px',
                            }}
                            onClick={(ev) => {
                              ev.preventDefault();
                              setMenuTitleToRemove(menuTitle);
                              setRemoveSectionPopupAnchorEl(ev.target);
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href="#"
        style={{
          fontWeight: 'bold',
          textDecoration: 'none',
          margin: 'auto 0',
        }}
        onClick={(ev) => {
          ev.preventDefault();
          setNewSectionModalAnchorEl(ev.target);
        }}>
        + Add new section
      </a>

      <Grid container>
        <Grid item xs={12} style={{ margin: '15px 0', padding: '6px' }}>
          <Button variant="contained" onClick={handleSave} disabled={!isDirty}>
            Save
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate(-1)}
            style={{ marginLeft: '6px' }}>
            Cancel
          </Button>
        </Grid>
      </Grid>
      {newSectionModalAnchorEl && (
        <SpaceRecipesNewSectionModal
          anchorEl={newSectionModalAnchorEl}
          availableSections={availableMenuTitles}
          onSave={onAddNewMenuTitle}
          onDismiss={() => setNewSectionModalAnchorEl(null)}
        />
      )}
      <DeleteCardConfirmPopover
        visible={!!removeSectionPopupAnchorEl}
        anchorEl={removeSectionPopupAnchorEl}
        title="Delete section"
        message="The section will no longer be shown on the My Day screen. Are you sure?"
        height={170}
        onConfirmDelete={() => {
          onRemoveMenuTitle(menuTitleToRemove);
        }}
        onDismiss={() => setRemoveSectionPopupAnchorEl(null)}
      />
    </div>
  );
};

export default SpaceRecipesConfigView;
